import { Translations } from './../../localization/translations.d';
import { Payment, Product, DeliveryAddress, InvoiceAddress } from '../types/Payment';
import { getLocaleCurrencyInfo } from '../../utils/getCurrencyInfo';

// Function to get payment property email
export const getPaymentPropertyEmail = (paymentInfo: Payment): string => {
  return paymentInfo?.products?.[0]?.gift?.email || window?.crm_user?.user?.email || '';
};

// Function to get payment type based on sales funnel
export const getPaymentType = (salesFunnel: string | null | undefined): string => {
  return salesFunnel === 'sample'
    ? 'for-me'
    : salesFunnel === 'business'
      ? 'business'
      : salesFunnel === 'eshop' // sk eshop
        ? 'eshop'
        : salesFunnel === null || salesFunnel === 'gift' // 2nd check for SK
          ? 'gift'
          : '';
};

// Function to check if payment is failed
export const isPaymentFailed = (payment: Payment): boolean => {
  return (
    ((payment.payment_gateway === 'gopay' || payment.payment_gateway === 'gopay_recurring') &&
      payment.status === 'form') ||
    payment.status === 'fail' ||
    payment.status === 'timeout'
  );
};

// Function to get payment status message
export const getPaymentStatusMessage = (payment: Payment, translations: Translations): string => {
  switch (payment.status) {
    case 'form':
      return translations?.orderLabels?.formStatus || 'In progress';
    case 'paid':
      return translations?.orderLabels?.paidStatus || 'Paid';
    case 'timeout':
      return translations?.orderLabels?.timeOutStatus || 'Payment is timed out';
    case 'fail':
      return translations?.orderLabels?.failedStatus || 'Payment failed';
    default:
      return 'n/a';
  }
};

// Function to format payment amount
export const formatPaymentAmount = (item: Payment | Product): string => {
  const amount = item.amount || 0;
  const { symbol: currencySymbol } = getLocaleCurrencyInfo();

  return `${amount} ${currencySymbol}`;
};

// Function to get payment method
export const getPaymentMethod = (payment: Payment, translations: Translations): string => {
  switch (payment.payment_gateway) {
    case 'gopay':
      return translations?.orderLabels?.gopayPaymentMethod || 'GoPay';
    case 'gopay_recurring':
      return translations?.orderLabels?.gopayPaymentMethod || 'GoPay';
    case 'bank_transfer':
      return translations?.orderLabels?.bankTransferPaymentMethod || 'Bank Transfer';
    default:
      return payment.payment_gateway || 'n/a';
  }
};

// Function to get delivery address from payment
export const getDeliveryAddressFromPayment = (payment: Payment): DeliveryAddress => {
  const deliveryAddress =
    payment.subscription?.delivery_address ||
    (payment.products && payment.products.length > 0 && payment.products[0].gift?.delivery_address);

  if (deliveryAddress) {
    const { first_name, last_name, address, city, zip, country } = deliveryAddress;

    return {
      first_name: first_name ?? '-',
      last_name: last_name ?? '-',
      address: address ?? '-',
      city: city ?? '-',
      zip: zip ?? '-',
      country: country ?? '-',
    };
  }

  return {
    first_name: '-',
    last_name: '-',
    address: '-',
    city: '-',
    zip: '-',
    country: '-',
  };
};

// Function to get invoice address from payment
export const getInvoiceAddressFromPayment = (payment: Payment): InvoiceAddress => {
  const invoiceAddress = payment.addresses?.invoice;

  if (invoiceAddress) {
    const {
      first_name,
      last_name,
      address,
      number,
      city,
      zip,
      country,
      phone_number,
      company_name,
      company_id,
      tax_id,
      vat_id,
    } = invoiceAddress;

    return {
      first_name: first_name ?? '-',
      last_name: last_name ?? '-',
      address: address ?? '-',
      number: number ?? null,
      city: city ?? '-',
      zip: zip ?? '-',
      country: country ?? '-',
      phone_number: phone_number ?? null,
      company_name: company_name ?? null,
      company_id: company_id ?? null,
      tax_id: tax_id ?? null,
      vat_id: vat_id ?? null,
    };
  }

  return {
    first_name: '-',
    last_name: '-',
    address: '-',
    number: null,
    city: '-',
    zip: '-',
    country: '-',
    phone_number: null,
    company_name: null,
    company_id: null,
    tax_id: null,
    vat_id: null,
  };
};
